import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { getIntegrationProviderDisplayName } from '../../../models/utils/integrationUtils';
import { ISuspendedIntegrationSummary } from '../../../queries/Admin/IntegrationsHealth';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { Pill } from '../../../web/components-v2/Pill';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../web/components/Tooltip';
import { CollapsibleProviderSection } from './CollapsibleProviderSection';
import { SetupRequirements } from './SetupRequirements';
import { recentActivityUrl } from './integrationUrls';
import { styleSheet } from './styles';
import {
	IRetryCandidate,
	candidateKeysForProvider,
	hasRetryableForProvider,
	retryKey,
	useRetrySelection,
} from './useRetrySelection';

interface IProps {
	groups: Map<Api.IntegrationProvider, ISuspendedIntegrationSummary[]>;
	expandGroups: boolean;
	selection: ReturnType<typeof useRetrySelection>;
	candidates: IRetryCandidate[];
	canRetry: boolean;
	isRetrying: boolean;
	onRetry: () => void;
}

export const SuspendedWithErrorSection: React.FC<IProps> = ({
	groups,
	expandGroups,
	selection,
	candidates,
	canRetry,
	isRetrying,
	onRetry,
}) => {
	const allKeys = React.useMemo(() => candidates.map(retryKey), [candidates]);

	return (
		<>
			{canRetry ? (
				<div className={css(styleSheet.toolbar)}>
					<Checkbox
						checked={selection.checkedStateFor(allKeys)}
						onCheckedChange={checked => selection.setManyChecked(allKeys, checked === true)}
						aria-label='Select all retryable integrations'
						disabled={selection.retryableCount === 0}
					/>
					<span className={css(styleSheet.selectionCount)}>
						{selection.selectedCount} of {selection.retryableCount} retryable selected
					</span>
					<span className={css(styleSheet.toolbarSpacer)} />
					<span className={css(styleSheet.retryHint)}>Resets to Idle, then runs an incremental sync</span>
					<Button variant='primary' size='sm' disabled={selection.selectedCount === 0 || isRetrying} onClick={onRetry}>
						{isRetrying ? 'Retrying…' : 'Retry selected'}
					</Button>
				</div>
			) : null}

			{Array.from(groups.entries()).map(([provider, providerRows]) => {
				const groupKeys = candidateKeysForProvider(candidates, provider);
				const groupState = selection.checkedStateFor(groupKeys);
				const hasRetryable = hasRetryableForProvider(candidates, provider);
				return (
					<CollapsibleProviderSection
						key={provider}
						title={getIntegrationProviderDisplayName(provider)}
						defaultOpen={expandGroups}
						leadingControl={
							canRetry ? (
								<Checkbox
									checked={groupState}
									onCheckedChange={checked => selection.setManyChecked(groupKeys, checked === true)}
									disabled={!hasRetryable}
									aria-label={`Select all retryable ${getIntegrationProviderDisplayName(provider)} integrations`}
								/>
							) : null
						}
						statusChip={<Pill variant='tag' color='red' label={`${providerRows.length}`} size='small' />}
					>
						<table className={css(styleSheet.table)}>
							<colgroup>
								<col className={css(styleSheet.selectCell)} />
								<col className={css(styleSheet.colAccount)} />
								<col className={css(styleSheet.colError)} />
								<col className={css(styleSheet.colSuspended)} />
								<col className={css(styleSheet.colActions)} />
							</colgroup>
							<thead>
								<tr>
									<th className={css(styleSheet.th)} aria-label='Select' />
									<th className={css(styleSheet.th)}>Account</th>
									<th className={css(styleSheet.th)}>Error</th>
									<th className={css(styleSheet.th)}>Failed</th>
									<th className={css(styleSheet.th)}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{providerRows.map(row => (
									<SuspendedRow
										key={`${row.accountId}-${row.provider}`}
										row={row}
										selection={selection}
										canRetry={canRetry}
									/>
								))}
							</tbody>
						</table>
					</CollapsibleProviderSection>
				);
			})}
		</>
	);
};

const SuspendedRow: React.FC<{
	row: ISuspendedIntegrationSummary;
	selection: ReturnType<typeof useRetrySelection>;
	canRetry: boolean;
}> = ({ row, selection, canRetry }) => {
	const target = { accountId: row.accountId, provider: row.provider };
	const url = recentActivityUrl(row.accountId, row.provider);

	return (
		<tr>
			{canRetry ? (
				<td className={css(styleSheet.td)}>
					{row.needsConfiguration ? (
						<Tooltip>
							<TooltipTrigger asChild>
								<span tabIndex={0}>
									<Checkbox
										checked={false}
										disabled
										aria-label={`${row.companyName ?? row.accountId} cannot be retried`}
									/>
								</span>
							</TooltipTrigger>
							<TooltipContent>
								Setup is incomplete, so the sync job stops at the same check. Fix the configuration first.
							</TooltipContent>
						</Tooltip>
					) : (
						<Checkbox
							checked={selection.isSelected(target)}
							onCheckedChange={checked => selection.toggleRow(target, checked === true)}
							aria-label={`Select ${row.companyName ?? row.accountId}`}
						/>
					)}
				</td>
			) : null}
			<td className={css(styleSheet.td)}>
				<Link className={css(styleSheet.accountLink)} to={url}>
					{row.companyName ?? row.accountId}
				</Link>
			</td>
			<td className={css(styleSheet.td)}>
				{row.needsConfiguration && row.requirements?.length ? (
					<SetupRequirements
						requirements={row.requirements}
						heading='Also blocked by configuration'
						footnote='Retrying will not clear this.'
					/>
				) : row.reason ? (
					<span className={css(styleSheet.reasonText)}>{row.reason}</span>
				) : (
					<span className={css(styleSheet.mutedText)}>No reason recorded</span>
				)}
			</td>
			<td className={css(styleSheet.td, styleSheet.mutedText)}>
				{row.suspendedAt ? moment(row.suspendedAt).fromNow() : '—'}
			</td>
			<td className={css(styleSheet.td)}>
				<Button asChild variant='secondary' size='sm' styles={[styleSheet.actionButton]}>
					<Link to={url}>View activity</Link>
				</Button>
			</td>
		</tr>
	);
};
